import { createSlice } from "@reduxjs/toolkit";

/**
 * The customer book.
 *
 * Two screens read this: the Customers tab, and the cart's invoice picker.
 * Whichever loads the list first fills it, and `isCustomerDataFetched` stops
 * the other fetching it again.
 *
 * `transactionsByCustomer` is keyed by customer id and filled lazily — a book
 * with two hundred names should not pull two hundred ledgers to show one.
 *
 * Like every slice, this is wiped on logout by the reset in store.js. One
 * business's book must never be visible to the next.
 */
const initialState = {
  customers: [],
  isCustomerDataFetched: false,
  transactionsByCustomer: {},
};

export const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    setCustomers: (state, action) => {
      state.customers = action.payload || [];
    },

    setCustomerDataFetched: (state, action) => {
      state.isCustomerDataFetched = action.payload;
      // Invalidating the list invalidates the ledgers with it — they were read
      // alongside those balances, and an invoice order that moved one has
      // almost certainly moved the other.
      if (!action.payload) state.transactionsByCustomer = {};
    },

    addCustomer: (state, action) => {
      if (action.payload) state.customers.unshift(action.payload);
    },

    updateCustomer: (state, action) => {
      const { _id, updatedCustomer } = action.payload;
      const index = state.customers.findIndex((customer) => customer._id === _id);
      if (index !== -1) {
        state.customers[index] = { ...state.customers[index], ...updatedCustomer };
      }
    },

    setCustomerTransactions: (state, action) => {
      const { customerId, transactions } = action.payload;
      state.transactionsByCustomer[customerId] = transactions || [];
    },

    /**
     * A new line, and the balance it moved.
     *
     * The server has already applied the same arithmetic with `$inc`, so this
     * is the screen catching up rather than deciding — but doing it here means
     * the figure changes the instant the line appears, instead of after a
     * refetch nobody asked for.
     */
    addCustomerTransaction: (state, action) => {
      const { customerId, transaction } = action.payload;
      const existing = state.transactionsByCustomer[customerId];
      if (Array.isArray(existing)) existing.push(transaction);

      const index = state.customers.findIndex((customer) => customer._id === customerId);
      if (index !== -1) {
        const delta =
          Number(transaction.debit || 0) - Number(transaction.credit || 0);
        const next = Number(state.customers[index].balance || 0) + delta;
        state.customers[index].balance = Math.round(next * 100) / 100;
      }
    },

    /**
     * A corrected line, and the difference it made.
     *
     * The balance moves by the DIFFERENCE, never the new figure: a $50 charge
     * corrected to $40 is a $10 move. The server does exactly this arithmetic
     * with the before-and-after values, so the two stay in step.
     */
    replaceCustomerTransaction: (state, action) => {
      const { customerId, transaction } = action.payload;
      const list = state.transactionsByCustomer[customerId];
      if (!Array.isArray(list)) return;

      const index = list.findIndex((entry) => entry._id === transaction._id);
      if (index === -1) return;

      const before = Number(list[index].debit || 0) - Number(list[index].credit || 0);
      const after = Number(transaction.debit || 0) - Number(transaction.credit || 0);
      list[index] = { ...list[index], ...transaction };

      const customerIndex = state.customers.findIndex((c) => c._id === customerId);
      if (customerIndex !== -1) {
        const next = Number(state.customers[customerIndex].balance || 0) + (after - before);
        state.customers[customerIndex].balance = Math.round(next * 100) / 100;
      }
    },

    /** A removed line puts back exactly what it moved. */
    removeCustomerTransaction: (state, action) => {
      const { customerId, transactionId } = action.payload;
      const list = state.transactionsByCustomer[customerId];
      if (!Array.isArray(list)) return;

      const removed = list.find((entry) => entry._id === transactionId);
      if (!removed) return;

      state.transactionsByCustomer[customerId] = list.filter(
        (entry) => entry._id !== transactionId
      );

      const customerIndex = state.customers.findIndex((c) => c._id === customerId);
      if (customerIndex !== -1) {
        const delta = Number(removed.debit || 0) - Number(removed.credit || 0);
        const next = Number(state.customers[customerIndex].balance || 0) - delta;
        state.customers[customerIndex].balance = Math.round(next * 100) / 100;
      }
    },

    /** Used after an invoice order, where the order controller moved it. */
    setCustomerBalance: (state, action) => {
      const { _id, balance } = action.payload;
      const index = state.customers.findIndex((customer) => customer._id === _id);
      if (index !== -1) state.customers[index].balance = balance;
    },
  },
});

export const {
  setCustomers,
  setCustomerDataFetched,
  addCustomer,
  updateCustomer,
  setCustomerTransactions,
  addCustomerTransaction,
  replaceCustomerTransaction,
  removeCustomerTransaction,
  setCustomerBalance,
} = customerSlice.actions;

export default customerSlice.reducer;
